import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '@/lib/api';
import { ArrowLeft, ArrowRight, Check, ChefHat } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const PLANS = [
  { id: 'basic', name: 'Basic', price: 150, desc: 'Simple, healthy meals for the week', meals: 1 },
  { id: 'standard', name: 'Standard', price: 250, desc: 'Full-service prep with variety', meals: 2 },
  { id: 'premium', name: 'Premium', price: 400, desc: 'Gourmet-level meals with premium ingredients', meals: 3 },
];

const DIETS = ['Organic Chicken', 'Wild Salmon', 'Fresh Vegetables', 'Brown Rice', 'Quinoa', 'Grass-Fed Beef', 'Tofu', 'Avocado', 'Sweet Potato', 'Kale'];

export default function MealPlanWizard() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ plan_type: '', frequency: 'weekdays', start_date: '', duration_weeks: 4, dietary_preferences: [], notes: '' });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const plan = PLANS.find(p => p.id === form.plan_type);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await API.post('/meal-plans', { ...form, meals_per_day: plan?.meals || 2 });
      setDone(true);
      toast.success('Meal plan created!', { description: `Your ${plan?.name} plan starts on ${form.start_date}` });
    } catch {
      toast.error('Failed to create meal plan');
    }
    setLoading(false);
  };

  const toggleDiet = (d) => {
    setForm(f => ({ ...f, dietary_preferences: f.dietary_preferences.includes(d) ? f.dietary_preferences.filter(x => x !== d) : [...f.dietary_preferences, d] }));
  };

  if (done) {
    return (
      <div className="fade-in max-w-lg mx-auto text-center py-20" data-testid="meal-plan-success">
        <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4"><Check className="w-8 h-8 text-green-600" /></div>
        <h1 className="text-2xl font-extrabold text-[#222] font-[Manrope]">Meal Plan Created!</h1>
        <p className="text-[#717171] mt-2">Your {plan?.name} plan starts on {form.start_date}. Pay via Zelle or Venmo.</p>
        <Button onClick={() => navigate('/bookings')} className="mt-6 bg-[#E05A47] hover:bg-[#C54A39] text-white rounded-lg btn-press" data-testid="view-my-bookings-btn">View My Bookings</Button>
      </div>
    );
  }

  const steps = [
    // Step 0: Choose plan
    <div key="plan" className="space-y-4" data-testid="step-plan">
      <h2 className="text-xl font-bold text-[#222] font-[Manrope]">Choose Your Plan</h2>
      <div className="space-y-3">
        {PLANS.map(p => (
          <button key={p.id} onClick={() => setForm(f => ({ ...f, plan_type: p.id }))} className={`w-full text-left rounded-xl border-2 p-5 transition-all ${form.plan_type === p.id ? 'border-[#E05A47] bg-[#E05A47]/5' : 'border-[#EBEBEB] hover:border-[#222]/20'}`} data-testid={`plan-${p.id}`}>
            <div className="flex justify-between items-start">
              <div>
                <p className="font-bold text-[#222] font-[Manrope]">{p.name}</p>
                <p className="text-sm text-[#717171] mt-0.5">{p.desc}</p>
                <p className="text-xs text-[#717171] mt-1">{p.meals} meal{p.meals > 1 ? 's' : ''}/day</p>
              </div>
              <p className="text-lg font-extrabold text-[#222] font-[Manrope]">${p.price}<span className="text-xs font-normal text-[#717171]">/wk</span></p>
            </div>
          </button>
        ))}
      </div>
    </div>,
    // Step 1: Schedule
    <div key="schedule" className="space-y-4" data-testid="step-schedule">
      <h2 className="text-xl font-bold text-[#222] font-[Manrope]">Schedule</h2>
      <div>
        <Label className="text-sm font-medium text-[#222]">Frequency</Label>
        <div className="flex gap-2 mt-1.5">
          {['weekdays', 'daily', '3x/week'].map(f => (
            <button key={f} onClick={() => setForm(prev => ({ ...prev, frequency: f }))} className={`px-4 py-2 rounded-lg border text-sm font-medium capitalize transition-all ${form.frequency === f ? 'border-[#E05A47] bg-[#E05A47]/5 text-[#E05A47]' : 'border-[#EBEBEB] text-[#717171]'}`} data-testid={`freq-${f}`}>
              {f}
            </button>
          ))}
        </div>
      </div>
      <div>
        <Label className="text-sm font-medium text-[#222]">Start Date</Label>
        <Input type="date" value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} className="mt-1.5 rounded-lg border-[#EBEBEB] h-11" data-testid="start-date-input" />
      </div>
      <div>
        <Label className="text-sm font-medium text-[#222]">Duration</Label>
        <div className="flex gap-2 mt-1.5">
          {[2, 4, 8, 12].map(w => (
            <button key={w} onClick={() => setForm(f => ({ ...f, duration_weeks: w }))} className={`px-4 py-2 rounded-lg border text-sm font-medium transition-all ${form.duration_weeks === w ? 'border-[#E05A47] bg-[#E05A47]/5 text-[#E05A47]' : 'border-[#EBEBEB] text-[#717171]'}`} data-testid={`duration-${w}`}>
              {w} weeks
            </button>
          ))}
        </div>
      </div>
    </div>,
    // Step 2: Preferences
    <div key="prefs" className="space-y-4" data-testid="step-preferences">
      <h2 className="text-xl font-bold text-[#222] font-[Manrope]">Dietary Preferences</h2>
      <div className="flex flex-wrap gap-2">
        {DIETS.map(d => (
          <button key={d} onClick={() => toggleDiet(d)} className={`px-3.5 py-2 rounded-full border text-sm font-medium transition-all ${form.dietary_preferences.includes(d) ? 'border-[#E05A47] bg-[#E05A47]/10 text-[#E05A47]' : 'border-[#EBEBEB] text-[#717171]'}`} data-testid={`diet-${d.toLowerCase().replace(/\s/g, '-')}`}>
            {d}
          </button>
        ))}
      </div>
      <div>
        <Label className="text-sm font-medium text-[#222]">Special Notes</Label>
        <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={3} placeholder="Allergies, preferences..." className="mt-1.5 w-full rounded-lg border border-[#EBEBEB] px-4 py-3 text-sm focus:ring-2 focus:ring-[#E05A47] focus:border-transparent" data-testid="notes-input" />
      </div>
    </div>,
    // Step 3: Review
    <div key="review" className="space-y-4" data-testid="step-review">
      <h2 className="text-xl font-bold text-[#222] font-[Manrope]">Review Your Plan</h2>
      <div className="bg-[#F7F7F7] rounded-xl p-5 space-y-3">
        <Row label="Plan" value={plan?.name} />
        <Row label="Meals/Day" value={plan?.meals} />
        <Row label="Frequency" value={form.frequency} />
        <Row label="Start Date" value={form.start_date} />
        <Row label="Duration" value={`${form.duration_weeks} weeks`} />
        <Row label="Preferences" value={form.dietary_preferences.join(', ') || 'None'} />
        {form.notes && <Row label="Notes" value={form.notes} />}
        <div className="border-t border-[#EBEBEB] pt-3 flex justify-between font-bold text-[#222]">
          <span>Total</span>
          <span className="font-[Manrope]">${(plan?.price || 0) * form.duration_weeks}</span>
        </div>
        <p className="text-xs text-[#E05A47] font-semibold">Payment: Zelle or Venmo</p>
      </div>
    </div>,
  ];

  const canNext = step === 0 ? !!form.plan_type : step === 1 ? !!form.start_date : true;

  return (
    <div className="fade-in max-w-lg mx-auto" data-testid="meal-plan-wizard">
      <button onClick={() => step === 0 ? navigate(-1) : setStep(step - 1)} className="flex items-center gap-1.5 text-[#717171] hover:text-[#222] text-sm font-medium mb-6" data-testid="wizard-back">
        <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Back
      </button>

      {/* Progress */}
      <div className="flex gap-1.5 mb-8">
        {[0, 1, 2, 3].map(i => (
          <div key={i} className={`h-1 flex-1 rounded-full transition-all ${i <= step ? 'bg-[#E05A47]' : 'bg-[#EBEBEB]'}`} />
        ))}
      </div>

      {steps[step]}

      <div className="mt-8 flex justify-end">
        {step < 3 ? (
          <Button onClick={() => setStep(step + 1)} disabled={!canNext} className="bg-[#E05A47] hover:bg-[#C54A39] text-white rounded-lg font-semibold px-6 btn-press gap-2" data-testid="wizard-next">
            Continue <ArrowRight className="w-4 h-4" />
          </Button>
        ) : (
          <Button onClick={handleSubmit} disabled={loading} className="bg-[#E05A47] hover:bg-[#C54A39] text-white rounded-lg font-semibold px-6 btn-press" data-testid="wizard-submit">
            {loading ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : 'Confirm Plan'}
          </Button>
        )}
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-[#717171]">{label}</span>
      <span className="text-[#222] font-medium capitalize">{value}</span>
    </div>
  );
}
